import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { 
  Share2, Download, Copy, Check, Flame, 
  ExternalLink, MessageCircle, Star
} from 'lucide-react';
import { toast } from 'sonner';

const GAME_NAME = "Degen Battles";
const GAME_URL = typeof window !== 'undefined' ? window.location.origin : '';

// X (Twitter) icon component
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Telegram icon component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const rarityColors = {
  common: '#A0A0A0',
  rare: '#00CCFF',
  epic: '#FF00FF',
  legendary: '#FFD700',
};

const rarityEmojis = {
  common: '⚪',
  rare: '🔵',
  epic: '🟣',
  legendary: '🟡'
};

const ShareFlexModal = ({ isOpen, onClose, card, onBurn }) => {
  const [copied, setCopied] = React.useState(false);

  if (!card) return null;

  const rarity = card.rarity || 'common';
  const cardUrl = `${GAME_URL}/card/${card.id}`;
  
  // Dynamic share text based on rarity
  const getShareText = () => {
    if (rarity === 'legendary') {
      return `OMFG! 🤯 I just pulled a LEGENDARY ${card.character_name} in ${GAME_NAME}! Can you beat my deck? 🚀 #Degen #Solana #TCG`;
    } else if (rarity === 'epic') {
      return `Just scored an EPIC ${card.character_name} - "${card.title}" 💜 My collection is getting serious! #Degen #Solana`;
    } else if (rarity === 'rare') {
      return `New rare pull! ${card.character_name} joins my deck 💎 Who wants to battle? #DegenBattles`;
    } else {
      return `Got my starter ${card.character_name}. Who wants to battle? ⚔️ #DegenBattles`;
    }
  };

  const shareText = getShareText();

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${card.image_base64}`;
    link.download = `degen-${card.character_name.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.click();
    toast.success('Image downloaded!');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // X (Twitter) Share
  const handleXShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(cardUrl)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  // Telegram Share
  const handleTelegramShare = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(cardUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(telegramUrl, '_blank');
  };

  // Native Share (Mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${card.character_name} - ${GAME_NAME}`,
          text: shareText,
          url: cardUrl,
        });
        toast.success('Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      // Fallback to copy link
      handleCopyLink();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-[#0A0A0A] border-2 max-w-md"
        style={{ borderColor: rarityColors[rarity] }}
        data-testid="share-flex-modal"
      >
        <DialogHeader>
          <DialogTitle 
            className="font-anton text-3xl uppercase flex items-center gap-2"
            style={{ color: rarityColors[rarity] }}
          >
            <Share2 className="w-6 h-6" />
            SHARE YOUR FLEX
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Card Preview */}
          <div 
            className="relative rounded-xl overflow-hidden border-2"
            style={{ borderColor: rarityColors[rarity] }}
          >
            <img
              src={`data:image/png;base64,${card.image_base64}`}
              alt={card.character_name}
              className="w-full h-auto"
            />
            {/* Rarity Badge */}
            <div 
              className="absolute top-3 right-3 px-3 py-1 rounded-full font-anton text-sm uppercase flex items-center gap-1"
              style={{ 
                backgroundColor: `${rarityColors[rarity]}30`,
                color: rarityColors[rarity],
                border: `2px solid ${rarityColors[rarity]}`
              }}
            >
              <Star className="w-4 h-4" />
              {rarity}
            </div>
          </div>

          {/* Card Info */}
          <div className="text-center">
            <h3 className="font-anton text-2xl text-white">{card.character_name}</h3>
            <p className="font-bebas text-lg text-white/60">"{card.title}"</p>
            <div className="flex justify-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${
                    i < card.power_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Share Preview Text */}
          <div className="bg-[#121212] p-4 rounded-lg border border-white/10">
            <p className="font-space text-sm text-white/80">{shareText}</p>
          </div>

          {/* SHARE FLEX Buttons */}
          <div className="space-y-3">
            <p className="font-space text-xs text-white/40 uppercase tracking-wider text-center">
              Share to flex on your friends
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {/* X (Twitter) */}
              <Button
                onClick={handleXShare}
                className="bg-black hover:bg-black/80 text-white font-space uppercase border-2 border-white/20 hover:border-white/40"
                data-testid="share-x"
              >
                <XIcon className="w-5 h-5 mr-2" />
                Post on X
              </Button>

              {/* Telegram */}
              <Button
                onClick={handleTelegramShare}
                className="bg-[#0088cc] hover:bg-[#0077b5] text-white font-space uppercase"
                data-testid="share-telegram"
              >
                <TelegramIcon className="w-5 h-5 mr-2" />
                Telegram
              </Button>

              {/* Native Share (Mobile) */}
              <Button
                onClick={handleNativeShare}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-space uppercase"
                data-testid="share-native"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Share...
              </Button>

              {/* Copy Link */}
              <Button
                onClick={handleCopyLink}
                className="bg-neon-green hover:bg-neon-green/80 text-black font-space uppercase"
                data-testid="copy-link"
              >
                {copied ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
          </div>

          {/* Secondary Actions */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
            <Button
              onClick={handleDownload}
              className="bg-white/10 hover:bg-white/20 text-white font-space uppercase text-sm"
              data-testid="download-image"
            >
              <Download className="w-4 h-4 mr-2" />
              Save Image
            </Button>

            <Button
              onClick={onBurn}
              className="bg-red-600/20 hover:bg-red-600/40 border border-red-500 text-red-400 font-space uppercase text-sm"
              data-testid="burn-card-btn"
            >
              <Flame className="w-4 h-4 mr-2" />
              Burn Card
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareFlexModal;
